// ─── Browser-side error reporting ───────────────────────────────────────────────
// Posts to the v1-client-logs edge function, never to Better Stack directly — the
// source token would otherwise ship in a bundle every visitor downloads. See the
// header of supabase/functions/v1-client-logs/index.ts for why that matters.
//
// Before this existed, a React render crash was a white screen that left no trace
// anywhere: no console anyone would see, no edge log (the request never failed),
// nothing in Supabase. The first report was a person saying "the page is blank".
//
// Everything here is best-effort and silent. A failure to report an error must
// never produce a visible error, and must never recurse into reporting itself.

import { envValue } from './env'

const supabaseUrl = envValue(process.env.EXPO_PUBLIC_SUPABASE_URL)

export type ClientLogLevel = 'error' | 'warn' | 'info'

export interface ClientErrorReport {
  /** Screen, component or subsystem — becomes `web:<source>` in Better Stack. */
  source: string
  message: string
  error?: unknown
  level?: ClientLogLevel
  context?: Record<string, unknown>
}

interface WireEvent {
  level: ClientLogLevel
  source: string
  message: string
  error?: { name: string; message: string; stack?: string }
  context?: Record<string, unknown>
}

// ─── Loop and flood protection ──────────────────────────────────────────────────
// A crash inside a render loop can fire hundreds of identical errors a second.
// The server throttles too, but the request itself has to be stopped here or the
// browser spends the user's bandwidth on it.

const RECENT_WINDOW_MS = 10_000
const MAX_PER_WINDOW = 5
const recent = new Map<string, { count: number; first: number }>()

/** Set while a report is in flight, so a failure inside reporting cannot recurse. */
let reporting = false

function throttled(key: string): boolean {
  const now = Date.now()
  const entry = recent.get(key)
  if (!entry || now - entry.first >= RECENT_WINDOW_MS) {
    recent.set(key, { count: 1, first: now })
    return false
  }
  entry.count++
  return entry.count > MAX_PER_WINDOW
}

function toWireError(err: unknown): WireEvent['error'] {
  if (err instanceof Error) {
    return { name: err.name || 'Error', message: err.message ?? '', stack: err.stack }
  }
  if (err === undefined || err === null) return undefined
  return { name: 'NonError', message: String(err) }
}

/**
 * Records a client-side error. Fire-and-forget: returns immediately and never
 * rejects, so it is safe to call from a render path or an event handler.
 *
 * Redaction happens server-side in _shared/observability.ts, deliberately — the
 * rules must be identical for a browser stack and an edge-function stack, and one
 * copy of them cannot drift from the other.
 */
export function reportClientError(report: ClientErrorReport): void {
  try {
    if (!supabaseUrl || reporting) return

    const key = `${report.source}:${report.message}`.slice(0, 200)
    if (throttled(key)) return

    const event: WireEvent = {
      level: report.level ?? 'error',
      source: report.source,
      message: report.message,
      error: toWireError(report.error),
      context: report.context,
    }

    reporting = true
    // `keepalive` lets the report survive a navigation away from the crashing
    // page, which is exactly when a user reflexively closes the tab.
    void fetch(`${supabaseUrl}/functions/v1/v1-client-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true,
    })
      .catch(() => {
        // Offline, blocked, or the endpoint is down. Nothing to do — reporting an
        // error about failing to report an error is how you build an infinite loop.
      })
      .finally(() => {
        reporting = false
      })
  } catch {
    reporting = false
  }
}

/**
 * Installs the two global traps. Call once, from main.tsx, before the app renders.
 *
 * These catch what an ErrorBoundary structurally cannot: errors in event handlers,
 * in async callbacks, and in code that runs outside React's render tree entirely.
 */
export function initObservability(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (event: ErrorEvent) => {
    // Resource load failures (a broken <img>) also fire this event but carry no
    // `error`, and are not application faults worth alerting on.
    if (!event.error && !event.message) return
    reportClientError({
      source: 'window.onerror',
      message: event.message || 'uncaught error',
      error: event.error,
      context: {
        // The URL is scrubbed server-side; the line/column are what localise it
        // in a minified bundle.
        line: event.lineno,
        column: event.colno,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError({
      source: 'unhandledrejection',
      message:
        event.reason instanceof Error
          ? event.reason.message
          : `unhandled rejection: ${String(event.reason).slice(0, 200)}`,
      error: event.reason,
    })
  })
}

// ─── API failures ───────────────────────────────────────────────────────────────

/**
 * Reports a failed API call from lib/api.ts.
 *
 * Deliberately narrow about what counts as a fault:
 *   - a network failure (`status` absent) means the request never landed — that is
 *     always worth knowing, and is invisible from the server side by definition;
 *   - 5xx is a server fault;
 *   - 4xx is NOT reported. 401 during a normal token refresh, 404 on a deleted
 *     record and 400 on a validation error are all ordinary operation, and
 *     alerting on them would bury the real faults within a day.
 */
export function reportApiFailure(input: {
  path: string
  method: string
  status?: number
  detail?: unknown
}): void {
  const { path, method, status, detail } = input
  if (status !== undefined && status < 500) return

  reportClientError({
    source: 'api',
    message: status ? `${method} ${path} failed with ${status}` : `${method} ${path} could not be reached`,
    error: detail instanceof Error ? detail : undefined,
    context: {
      path,
      method,
      status: status ?? null,
      // Only the shape of the failure, not the response body — a body can contain
      // the very customer record the request was about.
      network_failure: status === undefined,
    },
  })
}
